import { Spacing } from '@persona-id/design-tokens';
import { LoaderIcon, XIcon } from '@persona-id/design-tokens/icons';
import { MouseEvent, ReactNode, useCallback, useEffect, useRef } from 'react';
import { DropzoneProps, FileRejection, useDropzone } from 'react-dropzone';
import { CSSTransition } from 'react-transition-group';
import Image from 'shared/components/Image';
import styled, { css, keyframes } from 'styled-components';

import { FileWithPreview, Preview } from 'dashboard/components-lib/FileUpload/types';
import Button from 'design-system/components/Button';
import { paragraphMixins } from 'design-system/mixins/text';
import { TransitionDuration } from 'design-system/variables/transitions';

const Container = styled('div')<{
  isDragActive: boolean;
  isDragReject: boolean;
}>`
  ${paragraphMixins.md};

  position: relative;
  border: 1px dashed ${({ theme }) => theme.color.border.primary};
  cursor: pointer;
  background: ${({ theme }) => theme.color.background.secondary};
  color: ${({ theme }) => theme.color.text.tertiary};
  svg > *[stroke] {
    stroke: ${({ theme }) => theme.color.text.tertiary};
  }
  height: 160px;
  width: 100%;
  border-radius: ${({ theme }) => theme.element.borderRadius};
  transition-duration: ${TransitionDuration.Fast};

  ${({ isDragReject, isDragActive, theme }) => {
    if (isDragReject) {
      return css`
        border-color: ${theme.color.variants.danger.default};
        background: ${theme.color.variants.danger.highlight};
        color: ${theme.color.variants.danger.default};
        svg > *[stroke] {
          stroke: ${theme.color.variants.danger.default};
        }
      `;
    } else if (isDragActive) {
      return css`
        background: ${theme.color.background.primary};
        color: ${theme.color.variants.primary.default};
        svg > *[stroke] {
          stroke: ${theme.color.variants.primary.default};
        }
      `;
    }
  }};

  > p {
    text-align: center;
  }
`;

export const DragContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  width: 100%;
  padding: 20px;
`;

const ThumbnailContainer = styled('aside')`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 20px;
`;

export const AspectRatioGuard = styled('div')`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Thumbnail = styled(Image)`
  height: auto;
  width: auto;
  max-height: 100%;
  max-width: 100%;
`;

const TextThumbnail = styled('p')`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.color.text.primary};
  height: 100%;
`;

const ClearButton = styled(Button)`
  position: absolute;
  top: ${Spacing[2]};
  right: ${Spacing[2]};
`;

const SpinnerContainer = styled('div')`
  position: absolute;
  cursor: default;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const TRANSITION_CSS = css`
  transition-duration: 0.2s;
  opacity: 0;

  .fade-enter & {
    opacity: 0;
  }
  .fade-enter-done & {
    opacity: 1;
  }
  .fade-exit & {
    opacity: 1;
  }
  .fade-exit-done & {
    opacity: 0;
  }
`;

const SpinnerBackdrop = styled('div')`
  position: absolute;
  height: 100%;
  width: 100%;
  background: ${({ theme }) => theme.color.background.primary};

  ${TRANSITION_CSS};

  .fade-enter-done & {
    opacity: 0.8;
  }
  .fade-exit & {
    opacity: 0.8;
  }
`;

const rotating = keyframes`
  from {
    transform:  rotate(0deg);
  }
  to {
    transform:  rotate(360deg);
  }
`;

const AnimatedLoaderIcon = styled(LoaderIcon)`
  z-index: 1;
  margin: ${Spacing[2]};
  animation: ${rotating} 1s linear infinite;

  ${TRANSITION_CSS};
`;

const CancelButton = styled('button')`
  ${paragraphMixins.md};

  background: none;
  cursor: pointer;
  z-index: 1;
  color: ${({ theme }) => theme.color.text.primary};

  ${TRANSITION_CSS};
`;

const DEFAULT_LABEL = <p>Drag and drop your file here</p>;
const DEFAULT_LABEL_DRAG = <p>Drop your file here</p>;
const DEFAULT_LABEL_REJECT = <p>Invalid file type</p>;

const getCSVRowCount = (content: string) => {
  // from https://stackoverflow.com/a/2953007
  // This does not handle 2+ newlines in one quoted string
  // Handling that case can be expensive on a large file and is uncommon
  const lines = content.split('\n');
  for (let i = lines.length - 1; i >= 0; i--) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (lines[i].match(/"/g)?.length % 2 === 1) {
      lines.splice(i - 1, 2, lines[i - 1] + lines[i]);
    }
  }
  return lines.length;
};

const predictCSVDelimiter = (content: string) => {
  const possibleDelimiters = [',', ';', '\t', '|'];
  let predictedDelimiter = ',';

  let maxColumnCount = 0;
  const firstRow = content.split('\n')[0];

  possibleDelimiters.forEach((delimiter) => {
    const columnCount = firstRow.split(delimiter).length;
    if (columnCount > maxColumnCount) {
      maxColumnCount = columnCount;
      predictedDelimiter = delimiter;
    }
  });

  return predictedDelimiter;
};

const getCSVHeaders = (content: string) => {
  const delimiter = predictCSVDelimiter(content);

  const lines = content.split('\n');
  return lines[0].split(delimiter).map((header) => header.trim());
};

// returns an array of the cells in the first non-header row
const getCSVDataPreview = (content: string) => {
  const delimiter = predictCSVDelimiter(content);

  const lines = content.split('\n');
  if (lines.length > 1) {
    return lines[1].split(delimiter).map((header) => header.trim());
  }

  return [];
};

export interface FileUploadProps
  extends Pick<DropzoneProps, 'accept' | 'minSize' | 'maxSize' | 'maxFiles' | 'disabled'> {
  clearable?: boolean;
  loading?: boolean;
  value?: FileWithPreview;
  className?: string;
  clearButton?: (
    onClear: (e: MouseEvent<HTMLButtonElement | HTMLOrSVGElement>) => void,
  ) => ReactNode;
  label?: ReactNode;
  labelDrag?: ReactNode;
  labelReject?: ReactNode;
  /**
   * `props.preview`
   *
   * Acts as a "poster" for when an initial value that isn't a `File` needs to be
   * displayed. For example, we can pass an URL for an existing image, which will
   * make `FilePicker` behave as if that were `props.value`, even if no `File` is
   * supplied. Note that clicking the clear button will trigger `props.onChange`,
   * and the preview should be removed when this occurs to reset to an empty state.
   */
  preview?: Preview;
  onChange?: (file: FileWithPreview | null) => void;
  onCancel?: () => void;
  onReject?: (fileRejections: FileRejection[]) => void;
}

const FileUpload = ({
  accept,
  clearable = true,
  loading,
  preview,
  value,
  className,
  clearButton,
  maxFiles = 1,
  maxSize = Infinity,
  label = DEFAULT_LABEL,
  labelDrag = DEFAULT_LABEL_DRAG,
  labelReject = DEFAULT_LABEL_REJECT,
  onChange,
  onCancel,
  onReject,
}: FileUploadProps) => {
  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onChange(null);
      } else {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        acceptedFiles.forEach((file: FileWithPreview) => {
          file.isImage = file.type.includes('image');
          const reader = new FileReader();
          reader.onload = function (e) {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            const contents = e.target.result;
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            const rowCt = getCSVRowCount(contents.toString());
            if (file.type === 'text/csv' && rowCt !== 0) {
              file.preview = `${file.name} (${rowCt} rows detected)`;
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              file.headers = getCSVHeaders(contents.toString());
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              file.dataPreview = getCSVDataPreview(contents.toString());
            } else {
              file.preview = file.isImage
                ? URL.createObjectURL(file)
                : `${file.name} (${file.size} bytes)`;
            }
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            onChange(file);
          };
          reader.readAsText(file);
        });
      }
    },
    [onChange],
  );

  const onClear = useCallback(
    (e: MouseEvent<HTMLButtonElement | HTMLOrSVGElement>) => {
      e.stopPropagation();
      onDropAccepted([]);
    },
    [onDropAccepted],
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDropAccepted,
    onDropRejected: onReject,
    accept,
    maxFiles,
    maxSize,
  });

  // Clean up file previews to avoid memory leak
  const prevFile = useRef<FileWithPreview>(null);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    prevFile.current = value;

    return () =>
      prevFile.current && prevFile.current.isImage && URL.revokeObjectURL(prevFile.current.preview);
  }, [value]);

  let displayedLabel = label;

  if (isDragReject) {
    displayedLabel = labelReject;
  } else if (isDragActive) {
    displayedLabel = labelDrag;
  }

  const previewOrValue = value || preview;

  return (
    <Container
      className={className}
      {...getRootProps()}
      id='file-upload-container'
      aria-label='Upload file'
      isDragActive={isDragActive}
      isDragReject={isDragReject}
    >
      <input {...getInputProps()} />

      <CSSTransition
        classNames='fade'
        in={loading}
        timeout={0}
        mountOnEnter={true}
        unmountOnExit={true}
      >
        <SpinnerContainer id='file-upload__spinner-container' onClick={(e) => e.stopPropagation()}>
          <AnimatedLoaderIcon />
          {onCancel && (
            <CancelButton id='file-upload__cancel-button' onClick={onCancel}>
              Cancel
            </CancelButton>
          )}
          <SpinnerBackdrop />
        </SpinnerContainer>
      </CSSTransition>
      {previewOrValue ? (
        <ThumbnailContainer>
          {clearable &&
            (clearButton ? (
              clearButton(onClear)
            ) : (
              <ClearButton
                id='file-upload__clear-button'
                aria-label='Remove image'
                onClick={onClear}
                width='icon'
                variant='inverse'
              >
                <XIcon />
              </ClearButton>
            ))}
          {previewOrValue.isImage ? (
            <AspectRatioGuard>
              <Thumbnail src={previewOrValue.preview} />
            </AspectRatioGuard>
          ) : (
            <TextThumbnail>{previewOrValue.preview}</TextThumbnail>
          )}
        </ThumbnailContainer>
      ) : (
        <DragContainer>{displayedLabel}</DragContainer>
      )}
    </Container>
  );
};

export default FileUpload;
